import { FC, useMemo } from 'react'
import { ShieldCheck, ShieldAlert, ShieldX } from 'lucide-react'
import { VideoAIAnalysis, CriterionEvaluation } from '@facesignai/api'

export type MetaScoreLevel = 'green' | 'orange' | 'red'

interface Props {
  videoAIAnalysis?: VideoAIAnalysis
}

interface StatusBadgeProps {
  level: MetaScoreLevel
}

export function computeMetaScore(
  videoAIAnalysis?: VideoAIAnalysis
): MetaScoreLevel | null {
  // If video analysis not ready yet, don't show (ReasoningLoader handles loading state)
  if (!videoAIAnalysis) return null

  const hasVideoIssues = Object.values(videoAIAnalysis).some(
    (c) => c.evaluation !== CriterionEvaluation.normal
  )

  // ORANGE case: video issues OR semantic risk is HIGH
  if (hasVideoIssues) return 'orange'

  // GREEN case: all checks pass
  return 'green'
}

// Count flagged and total signals for explanation text
function countSignals(videoAIAnalysis?: VideoAIAnalysis): {
  flagged: number
  total: number
} {
  if (!videoAIAnalysis) return { flagged: 0, total: 0 }
  const values = Object.values(videoAIAnalysis)
  return {
    flagged: values.filter((c) => c.evaluation !== CriterionEvaluation.normal)
      .length,
    total: values.length
  }
}

const config = {
  green: {
    bgSolid: 'bg-green-500',
    bgBadge: 'bg-green-100',
    textBadge: 'text-green-700',
    borderBadge: 'border-green-200',
    Icon: ShieldCheck,
    title: 'Verified',
    badgeText: 'Verified'
  },
  orange: {
    bgSolid: 'bg-orange-500',
    bgBadge: 'bg-orange-100',
    textBadge: 'text-orange-700',
    borderBadge: 'border-orange-200',
    Icon: ShieldAlert,
    title: 'High-Risk: Anomalies Detected',
    badgeText: 'High-Risk'
  },
  red: {
    bgSolid: 'bg-red-500',
    bgBadge: 'bg-red-100',
    textBadge: 'text-red-700',
    borderBadge: 'border-red-200',
    Icon: ShieldX,
    title: 'Not Verified',
    badgeText: 'Not Verified'
  }
}

// Status badge for the header
export const StatusBadge: FC<StatusBadgeProps> = ({ level }) => {
  const { bgBadge, textBadge, borderBadge, Icon, badgeText } = config[level]

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${bgBadge} ${textBadge} ${borderBadge} animate-fadeIn`}
    >
      <Icon size={14} strokeWidth={2} />
      {badgeText}
    </span>
  )
}

// Main banner component (Variant A style)
const MetaScoreSummary: FC<Props> = ({ videoAIAnalysis }) => {
  const level = useMemo(
    () => computeMetaScore(videoAIAnalysis),
    [videoAIAnalysis]
  )

  const { flagged, total } = useMemo(
    () => countSignals(videoAIAnalysis),
    [videoAIAnalysis]
  )

  // Memoize explanation text
  const explanation = useMemo(() => {
    switch (level) {
      case 'green':
        return 'All verification checks passed'
      case 'orange':
        return `${flagged} of ${total} signals flagged for review`
      case 'red':
        return 'The enrolled user was not detected in the session'
      default:
        return ''
    }
  }, [level, flagged, total])

  // Don't render if we don't have enough data yet
  if (!level) return null

  const { bgSolid, Icon, title } = config[level]

  return (
    <div className={`${bgSolid} rounded-lg p-5 mb-4 animate-fadeIn`}>
      <div className='flex items-center gap-4'>
        <div className='flex-shrink-0 text-white'>
          <Icon size={32} strokeWidth={2} />
        </div>
        <div className='flex-1'>
          <h3 className='text-xl font-bold text-white'>{title}</h3>
          <p className='text-sm text-white opacity-90'>{explanation}</p>
        </div>
      </div>
    </div>
  )
}

export default MetaScoreSummary
